// services/LocalMediaService.js
import Dexie from 'dexie';
import { uniqid , getBlobFromUrl} from '@/utils/utils'
import { isLikelyHtmlAudioPayload } from '@/utils/audioFormat'
import { runObsHtmlCacheRepairMigration } from '@/utils/audioCacheRepair'
import { applyCollectionWindow } from '@/utils/playlistLoading'
import { hasMusicFolderConflict, musicDay, musicMetadataPatch, musicUrlFolder } from '@/utils/musicMetadata'
const MAX_STORAGE_GB = 2;
const MAX_STORAGE_BYTES = MAX_STORAGE_GB * 1024 * 1024 * 1024; // 2GB
const OLDEST_FILES_TO_DELETE = 10; // Eliminar los 10 más antiguos cuando se exceda

class MediaDB extends Dexie {
  constructor() {
    super('soundDB');
    this.version(2).stores({
      // Store para archivos de medios (audio/video) - se descargan una vez
      mediaFiles: '++id, folder, code, title, initH, endH, url, urlimg, numerodia, blob, type, typeFile, size, createdAt, lastPlayed',
      // Store para programación de spots (horarios, metadata) - se actualiza siempre
      spotSchedule: '++id, spotCode, title, url, initH, endH, numerodia, slot,tipo, createdAt, updatedAt'
    });
  }
}

const db = new MediaDB();

class LocalMediaService {
  constructor() {
    this.db = db;
    this.localFiles = [];
    this.isInitialized = false;
    this.init();
  }

  // Inicialización básica
  async init() {
    if (this.isInitialized) return;
    //await this.loadFiles();
    this.isInitialized = true;
    //await this.db.mediaFiles.clear();
    //('LocalMediaService simplificado inicializado');
  }

  /**
   * Elimina respuestas HTML guardadas por error como audio. Esto repara de
   * forma automática los navegadores afectados cuando una URL de OBS relativa
   * fue resuelta contra la SPA del player.
   */
  async purgeInvalidAudioPayloads() {
    const mediaFiles = await this.db.mediaFiles.toArray()
    const invalidIds = []

    for (const media of mediaFiles) {
      const blob = media?.blob
      if (!blob?.slice) continue

      const headerBytes = new Uint8Array(await blob.slice(0, 128).arrayBuffer())
      if (isLikelyHtmlAudioPayload({ mimeType: blob.type, headerBytes })) {
        invalidIds.push(media.id)
      }
    }

    if (invalidIds.length > 0) {
      await this.db.mediaFiles.bulkDelete(invalidIds)

      console.warn(`[audio-cache] Se eliminaron ${invalidIds.length} archivos HTML guardados como audio.`)
    }

    // Migración de una sola ejecución. Debe correr también si una versión
    // anterior ya purgó los blobs pero dejó inflado el contador diario.
    runObsHtmlCacheRepairMigration(localStorage)

    return invalidIds.length
  }

  // Verificar y manejar límite de almacenamiento
  async checkStorageLimit(newFileSize = 0) {
  // FIX: calcular tamaño con metadata-only (el campo 'size' es suficiente)
  const metaFiles = await this.getFiles({ blobsIncluded: false })
  const currentUsage = metaFiles.reduce((sum, file) => sum + (file.size || 0), 0)
  const projectedUsage = currentUsage + newFileSize

  if (projectedUsage > MAX_STORAGE_BYTES) {
    console.warn('Límite de almacenamiento alcanzado (2GB)')
    await this.cleanupExcessStorage()

    await this.loadFiles()

    const newUsage = this.localFiles.reduce((sum, file) => sum + (file.size || 0), 0)
    if (newFileSize > 0 && newUsage + newFileSize > MAX_STORAGE_BYTES) {
      throw new Error('No hay espacio suficiente. Limpieza completada pero límite alcanzado.')
    }
  }
}

  // Limpiar exceso de almacenamiento (eliminar archivos más antiguos)
  async cleanupExcessStorage() {
    //('Limpiando exceso de almacenamiento...');
    
    // Ordenar por fecha de creación (más antiguos primero)
    const sortedFiles = await this.db.mediaFiles
      .orderBy('createdAt')
      .reverse()
      .toArray();

    let freedSpace = 0;
    const filesToDelete = [];

    // Eliminar los más antiguos hasta liberar suficiente espacio
    for (const file of sortedFiles) {
      if (freedSpace < MAX_STORAGE_BYTES * 0.2) { // Liberar al menos 20% del límite
        filesToDelete.push(file.id);
        freedSpace += file.size || 0;
      } else {
        break;
      }
    }

    if (filesToDelete.length > 0) {
      await this.db.mediaFiles.bulkDelete(filesToDelete);
      //(`Eliminados ${filesToDelete.length} archivos antiguos, liberados ${this.formatSize(freedSpace)}`);
    }

    return filesToDelete;
  }

  // Guardar archivo en IndexedDB
  // saveFile(objeto) {
  //   try {
  //     // Verificar límite antes de guardar
  //     await this.checkStorageLimit(objeto.size || 0);
  //     const id = await this.db.mediaFiles.put({
  //       ...objeto
  //     });
      
      
  //     // Recargar lista para mantener actualizada
  //     await this.loadFiles();

  //     //(`Archivo guardado con nombre: ${objeto.title}`);
  //     return id;
  //   } catch (error) {
  //     console.error('Error guardando archivo:', error);
  //     throw error;
  //   }
  // }

  /**
   * Detecta si una URL es de YouTube
   * @param {String} url - URL a verificar
   * @returns {Boolean} - true si es YouTube
   */
  isYouTubeUrl(url) {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.includes('youtube.com') ||
           lowerUrl.includes('youtu.be') ||
           lowerUrl.includes('youtube-nocookie.com');
  }

  /**
   * Detecta si una URL es un stream (HLS, DASH, etc.)
   * @param {String} url - URL a verificar
   * @returns {Boolean} - true si es un stream
   */
  isStreamUrl(url) {
    if (!url) return false;

    const streamExtensions = ['.m3u8', '.mpd'];
    const streamProtocols = ['rtmp://', 'rtsp://', 'hls://', 'dash://'];

    const lowerUrl = url.toLowerCase();

    // Verificar extensiones de streaming
    const hasStreamExtension = streamExtensions.some(ext => lowerUrl.includes(ext));

    // Verificar protocolos de streaming
    const hasStreamProtocol = streamProtocols.some(protocol => lowerUrl.startsWith(protocol));

    return hasStreamExtension || hasStreamProtocol;
  }


  // Cursor iteration retains no array of audio blobs (large branch libraries).
  async auditMusicFolders() {
    const folders = new Map(), catalogFolders = new Set(), conflictingRecords = []
    let music = 0, conflicts = 0, withoutAudio = 0, unknownUrls = 0
    await this.db.mediaFiles.each(row => {
      if (row.type !== 'music') return
      music++
      const urlFolder = musicUrlFolder(row.url)
      if (urlFolder) catalogFolders.add(urlFolder)
      else unknownUrls++
      const name = typeof row.folder === 'string' ? row.folder : '(sin carpeta)'
      const stats = folders.get(name) || { folder: name, tracks: 0, conflicts: 0, withoutAudio: 0 }
      stats.tracks++
      if (hasMusicFolderConflict(row)) {
        conflicts++; stats.conflicts++
        // Enough evidence to investigate leftovers without a console dump or
        // including query-string credentials from an unexpected stored URL.
        conflictingRecords.push({ id: row.id, title: row.title, folder: row.folder,
          urlFolder, objectPath: new URL(row.url).pathname })
      }
      if (!(row.blob instanceof Blob) || row.blob.size === 0) { withoutAudio++; stats.withoutAudio++ }
      folders.set(name, stats)
    })
    return { music, conflicts, withoutAudio, unknownUrls, conflictingRecords,
      folders: [...folders.values()].sort((a,b) => a.folder.localeCompare(b.folder)),
      catalogFolders: [...catalogFolders].sort() }
  }

  // Repair the proven folder only. Scheduling and playback history are not
  // properties of an OBS object; a repeated URL can have multiple time slots.
  async repairVerifiedMusicFolders(files, check) {
    const counts = { verified: 0, repaired: 0 }
    await this.db.transaction('rw', this.db.mediaFiles, async () => {
      check()
      for (const file of files) {
        if (!file?.link || musicUrlFolder(file.link) !== file.folder) throw new Error('invalid_catalog')
        const rows = await this.db.mediaFiles.where('url').equals(file.link).toArray()
        check()
        for (const row of rows) {
          if (row.type !== 'music') continue
          const patch = { metadataVerifiedAt: new Date().toISOString() }
          if (row.folder !== file.folder) {
            patch.folder = file.folder
            if (!row.metadataRepairBefore) patch.metadataRepairBefore = Object.fromEntries(
              ['folder', 'title', 'numerodia', 'initH', 'endH'].map(key => [key, row[key] ?? null]))
            counts.repaired++
          }
          await this.db.mediaFiles.update(row.id, patch)
          counts.verified++
          check()
        }
      }
      check()
    })
    return counts
  }

  // Guardar múltiples archivos
  async reconcileMusicMetadata(files) {
    const presentUrls = new Set()
    // Atomic metadata-only repair; same URL is required, never match by title.
    await this.db.transaction('rw', this.db.mediaFiles, async () => {
      for (const file of files) {
        if (file.type !== 'music' || !file.url || hasMusicFolderConflict(file) || musicDay(file) === null) continue
        const existing = await this.db.mediaFiles.where('url').equals(file.url).toArray()
        for (const row of existing) {
          if (row.type !== 'music') continue
          const patch = musicMetadataPatch(file)
          const changed = ['folder', 'title', 'numerodia', 'initH', 'endH'].some(key =>
            String(row[key]) !== String(patch[key]))
          if (changed) {
            // Keep the first repair preimage in the same atomic write; no audio copy.
            if (!row.metadataRepairBefore && (row.folder !== patch.folder || musicDay(row) === null)) {
              patch.metadataRepairBefore = Object.fromEntries(
                ['folder', 'title', 'numerodia', 'initH', 'endH'].map(key => [key, row[key] ?? null]))
            }
            await this.db.mediaFiles.update(row.id, patch)
          }
          presentUrls.add(file.url)
        }
      }
    })
    return presentUrls
  }

  async saveFiles(files) {
    // Validar que files sea un array
    if (!Array.isArray(files)) {
      console.error('saveFiles: se esperaba un array, se recibió:', typeof files);
      return [];
    }

    // Si el array está vacío, retornar sin procesar
    if (files.length === 0) {
      console.warn('saveFiles: array de archivos vacío, no hay nada que guardar');
      return [];
    }

    const savedIds = [];
    const skippedDuplicates = [];
    const failedFiles = [];

    // IMPORTANTE: Procesar TODOS los archivos, no solo 10
    // El límite anterior causaba bucles infinitos con muchos spots
    let init = 0, fin = files.length;

    for (init; init < fin; init++) {
      try {
        // Validar que el archivo tenga la estructura correcta
        if (!files[init] || typeof files[init] !== 'object') {
          console.error(`saveFiles: archivo en índice ${init} no es válido:`, files[init]);
          continue;
        }

        if (!files[init].url) {
          console.error(`saveFiles: archivo en índice ${init} no tiene URL:`, files[init]);
          continue;
        }

        if (files[init].type === 'music') {
          if (hasMusicFolderConflict(files[init]) || musicDay(files[init]) === null) {
            console.warn('[music-metadata] Rejected inconsistent music metadata', { title: files[init].title, folder: files[init].folder })
            continue
          }
          const existing = await this.reconcileMusicMetadata([files[init]])
          if (existing.has(files[init].url)) continue // repaired without download or daily quota
        }

        // Verificar si es URL de YouTube (no soportada)
        if (this.isYouTubeUrl(files[init].url)) {
          console.error(`❌ URLs de YouTube no están soportadas: ${files[init].title || files[init].url}`);
          console.error('⚠️ YouTube bloquea la reproducción directa por políticas de protección de contenido');
          console.error('💡 Alternativas: Usar YouTube Embed API, descargar el video, o usar un servicio proxy autorizado');
          continue;
        }

        // Verificar duplicados según el tipo de archivo
        let isDuplicate = false;

        if (files[init].type === 'spot') {
          // Para SPOTS: validar por (code + initH) o (url + initH)
          // Un mismo spot puede estar en diferentes horarios
          const existingSpots = await this.db.mediaFiles
            .where('type').equals('spot')
            .toArray();

          isDuplicate = existingSpots.some(existing => {
            const sameCode = existing.code === files[init].code;
            const sameUrl = existing.url === files[init].url;

            // Normalizar initH de ambos archivos a timestamp para comparación
            const existingInitH = existing.initH instanceof Date
              ? existing.initH.getTime()
              : (typeof existing.initH === 'number' ? existing.initH : new Date(existing.initH).getTime());

            const currentInitH = files[init].initH instanceof Date
              ? files[init].initH.getTime()
              : (typeof files[init].initH === 'number' ? files[init].initH : new Date(files[init].initH).getTime());

            const sameInitH = existingInitH === currentInitH;

            // Es duplicado si tiene (mismo code Y misma hora) O (misma url Y misma hora)
            return (sameCode && sameInitH) || (sameUrl && sameInitH);
          });
        } else {
          // Para MÚSICA: validar solo por code o url
          const existingByCode = files[init].code ?
            await this.db.mediaFiles.where('code').equals(files[init].code).first() : null;

          const existingByUrl = await this.db.mediaFiles.where('url').equals(files[init].url).first();

          isDuplicate = files[init].type === 'music' ? !!existingByUrl : !!(existingByCode || existingByUrl);
        }

        if (isDuplicate) {
          //(`⊘ Duplicado omitido: ${files[init].title || files[init].url}`);
          skippedDuplicates.push(files[init].title || files[init].url);
          continue;
        }

        // Detectar si es un stream
        const isStream = this.isStreamUrl(files[init].url);

        let blob = null;
        let size = 0;

        if (isStream) {
          // Para streams, NO descargar blob
          //(`📡 Stream detectado: ${files[init].title || files[init].url} - No se generará blob`);
          blob = null;
          size = 0;

          // Marcar explícitamente como streaming
          if (!files[init].typeFile) {
            files[init].typeFile = 'streaming';
          }
        } else {
          // Para archivos normales, descargar blob con manejo de errores robusto
          try {
            blob = await getBlobFromUrl(files[init].url);
          } catch (downloadError) {
            console.error(`⚠️ Error de descarga para ${files[init].title || files[init].url}:`, downloadError.message);
            failedFiles.push({
              index: init,
              title: files[init]?.title || 'sin título',
              error: `Error de descarga: ${downloadError.message}`
            });
            continue; // Continuar con el siguiente archivo
          }

          if (!blob) {
            console.error(`saveFiles: no se pudo obtener blob para ${files[init].title || files[init].url}`);
            failedFiles.push({
              index: init,
              title: files[init]?.title || 'sin título',
              error: 'Blob vacío o inválido'
            });
            continue; // Continuar con el siguiente archivo
          }

          size = blob?.size || 0;
        }

        files[init].blob = blob
        files[init].size = size;
        let id = await this.db.mediaFiles.put({...files[init]})
        files[init].blob = null
        savedIds.push(id)

        if (isStream) {
          //(`✓ Stream guardado (sin blob): ${files[init].title || files[init].url}`);
        } else {
          //(`✓ Archivo guardado: ${files[init].title || files[init].url}`);
        }
      } catch (error) {
        console.error(`Error guardando archivo ${init} (${files[init]?.title || 'sin título'}):`, error);
        failedFiles.push({
          index: init,
          title: files[init]?.title || 'sin título',
          error: error.message
        });
      }
    }

    //(`saveFiles completado: ${savedIds.length}/${fin} archivos guardados, ${skippedDuplicates.length} duplicados omitidos, ${failedFiles.length} errores`);
    if (skippedDuplicates.length > 0) {
      //('Duplicados:', skippedDuplicates.join(', '));
    }
    if (failedFiles.length > 0) {
      console.warn('Archivos con errores:', failedFiles.map(f => `${f.title} (${f.error})`).join(', '));
    }
    return savedIds;
  }
  
  // Obtener todos los archivos
 async getFiles(filters = {}) {
  const { type, folder, limit, blobsIncluded = true } = filters

  let query = this.db.mediaFiles

  if (type) {
    query = query.where('type').startsWith(type)
  } else if (folder) {
    query = query.where('folder').equals(folder)
  }

  const files = await query.toArray()

  if (files.length === 0) return []

  // FIX: si no se necesitan los blobs, excluirlos para no saturar la RAM.
  // Dexie no soporta proyección nativa, así que simplemente los borramos
  // del objeto retornado (el blob sigue solo en IndexedDB).
  const result = blobsIncluded
    ? files
    : files.map(f => {
        const { blob, ...meta } = f
        return meta
      })

  return limit ? result.slice(0, limit) : result
}

  // Obtener archivo por ID
  async getFileById(id) {
    return await this.db.mediaFiles.get(id);
  }

  // Obtener archivo por filename
  async getFileBy(value, prop='title', { limit, offset } = {}) {
    let collection = this.db.mediaFiles.where(prop).equals(value)
    if (prop === 'folder') {
      // Exclude contradictions before pagination, without deleting offline audio.
      collection = collection.filter(row => row.type !== 'music' || !hasMusicFolderConflict(row))
    }
    return await applyCollectionWindow(collection, { limit, offset }).toArray();
  }

  // Eliminar archivo por ID
  async deleteFile(id) {
    const file = await this.getFileById(id);
    if (!file) {
      throw new Error('Archivo no encontrado');
    }
    
    await this.db.mediaFiles.delete(id);
    await this.loadFiles();
    
    //(`Archivo eliminado: ${file.filename}`);
    return file;
  }

  // Eliminar múltiples archivos
  async deleteFiles(ids) {
    const deletedFiles = [];
    
    for (const id of ids) {
      try {
        const file = await this.deleteFile(id);
        deletedFiles.push(file);
      } catch (error) {
        console.error(`Error eliminando archivo ${id}:`, error);
      }
    }
    
    return deletedFiles;
  }

  // Cargar archivos desde IndexedDB (método privado)
  async loadFiles() {
  this.localFiles = await this.getFiles({ blobsIncluded: false })
}

  // Obtener estadísticas de almacenamiento
  getStorageStats() {
  // this.localFiles ya no tiene blobs, pero sí tiene el campo 'size'
  const totalSize = this.localFiles.reduce((sum, file) => sum + (file.size || 0), 0)
  const usedGB = totalSize / (1024 * 1024 * 1024)
  const percentage = ((usedGB / MAX_STORAGE_GB) * 100).toFixed(1)

  return {
    totalSize,
    usedGB,
    percentage,
    maxGB: MAX_STORAGE_GB,
    filesCount: this.localFiles.length,
    isNearLimit: usedGB > (MAX_STORAGE_GB * 0.9)
  }
}

  // Reproducir archivo (crear URL temporal)
  async playFile(id) {
    const file = await this.getFileById(id);
    if (!file || !file.blob) {
      throw new Error('Archivo no encontrado o corrupto');
    }

    const url = URL.createObjectURL(file.blob);
    
    // Actualizar fecha de última reproducción
    await this.db.mediaFiles.update(id, { lastPlayed: new Date() });
    
    // Emitir evento global para reproductor
    document.dispatchEvent(new CustomEvent('play-local-file', {
      detail: {
        id,
        url,
        type: file.type,
        title: file.title,
        filename: file.filename,
        cleanup: () => URL.revokeObjectURL(url)
      }
    }));

    await this.loadFiles(); // Actualizar lista
    return url;
  }

 

  // Limpiar archivos antiguos (manual)
  async cleanupOld(days = 30) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    
    const oldFiles = await this.db.mediaFiles
      .where('createdAt')
      .below(cutoff)
      .toArray();
    
    if (oldFiles.length > 0) {
      const ids = oldFiles.map(f => f.id);
      await this.db.mediaFiles.bulkDelete(ids);
      await this.loadFiles();
      //(`Eliminados ${oldFiles.length} archivos antiguos`);
    }
    
    return oldFiles.length;
  }

  // Utilidad para formatear tamaño
  formatSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Getter para archivos locales (para compatibilidad)
  get files() {
    return this.localFiles;
  }

  /**
   * Precarga los próximos N archivos de música de forma inteligente
   * Solo descarga si no existen ya en IndexedDB
   * @param {Array} musicList - Lista de metadata de música
   * @param {Number} count - Cantidad de tracks a precargar (default: 3)
   * @returns {Promise<Number>} Cantidad de archivos precargados
   */
  async preloadNextTracks(musicList, count = 3) {
    if (!Array.isArray(musicList) || musicList.length === 0) {
      console.warn('preloadNextTracks: no hay tracks para precargar')
      return 0
    }

    const tracksToPreload = musicList.slice(0, count)
    let preloadedCount = 0

    //(`🔄 Precargando próximos ${tracksToPreload.length} tracks...`)

    for (const track of tracksToPreload) {
      try {
        // Verificar si ya existe en IndexedDB
        const existing = await this.db.mediaFiles
          .where('url').equals(track.url)
          .first()

        if (existing && existing.blob) {
          //(`⊘ Track ya descargado: ${track.title}`)
          continue
        }

        // Detectar si es stream
        const isStream = this.isStreamUrl(track.url)

        if (isStream) {
          //(`📡 Stream detectado, sin precarga: ${track.title}`)
          continue
        }

        // Descargar blob con manejo de errores robusto
        let blob = null
        try {
          blob = await getBlobFromUrl(track.url)
        } catch (downloadError) {
          console.warn(`⚠️ Error de descarga al precargar ${track.title}:`, downloadError.message)
          continue // Continuar con el siguiente track
        }

        if (!blob) {
          console.warn(`⚠️ No se pudo precargar: ${track.title}`)
          continue // Continuar con el siguiente track
        }

        // Guardar en IndexedDB
        track.blob = blob
        track.size = blob.size || 0
        await this.db.mediaFiles.put(track)

        preloadedCount++
        //(`✅ Precargado: ${track.title} (${this.formatSize(blob.size)})`)

        // Pequeña pausa para no saturar
        await new Promise(resolve => setTimeout(resolve, 50))
      } catch (error) {
        console.error(`Error precargando ${track.title}:`, error)
      }
    }

    //(`✅ Precarga completada: ${preloadedCount}/${tracksToPreload.length} tracks`)
    return preloadedCount
  }

  // Verificar estado de almacenamiento
  getStorageStatus() {
    const stats = this.getStorageStats();
    return {
      ...stats,
      status: stats.usedGB > MAX_STORAGE_GB * 0.9 ? 'warning' : 'ok',
      message: stats.usedGB > MAX_STORAGE_GB * 0.9
        ? `Espacio bajo (${stats.percentage}%) - Se eliminarán archivos antiguos automáticamente`
        : `Espacio disponible: ${((1 - stats.usedGB / MAX_STORAGE_GB) * 100).toFixed(1)}%`
    };
  }

  // ============================================
  // MÉTODOS PARA PROGRAMACIÓN DE SPOTS
  // ============================================

  /**
   * Guarda o actualiza la programación de spots (sin descargar archivos)
   * @param {Array} spotSchedules - Array de programación de spots
   * @returns {Object} Resultado de la operación
   */
  async saveSpotSchedules(spotSchedules) {
    if (!Array.isArray(spotSchedules) || spotSchedules.length === 0) {
      console.warn('saveSpotSchedules: no hay programación para guardar');
      return { added: 0, updated: 0, unchanged: 0 };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Normalizar initH a número una sola vez para todos los schedules entrantes
    // ─────────────────────────────────────────────────────────────────────────
    const normalizeInitH = (v) =>
      v instanceof Date ? v.getTime()
      : typeof v === 'number' ? v
      : new Date(v).getTime()

    const normalizeEndH = (v) => {
      if (!v) return null
      if (v instanceof Date) return v.getTime()
      if (typeof v === 'number') return v
      return new Date(v).getTime()
    }

    // Construir mapa del servidor: clave = "spotCode_initH"
    const serverMap = new Map()
    for (const s of spotSchedules) {
      const initH = normalizeInitH(s.initH)
      serverMap.set(`${s.spotCode}_${initH}`, { ...s, initH, endH: normalizeEndH(s.endH) })
    }

    // Obtener TODOS los schedules locales
    const allLocal = await this.db.spotSchedule.toArray()
    const localMap = new Map()
    for (const l of allLocal) {
      const initH = normalizeInitH(l.initH)
      localMap.set(`${l.spotCode}_${initH}`, l)
    }

    const results = { added: 0, updated: 0, unchanged: 0, deleted: 0, errors: [] }
    const now = new Date()

    // ── 1. Eliminar locales que el servidor ya no envía ─────────────────────
    for (const [key, local] of localMap) {
      if (!serverMap.has(key)) {
        try {
          await this.db.spotSchedule.delete(local.id)
          // También eliminar el media file asociado
          const mediaInitH = normalizeInitH(local.initH)
          const orphanMedia = await this.db.mediaFiles
            .where('type').equals('spot')
            .and(m => {
              const mInitH = normalizeInitH(m.initH)
              return m.code === local.spotCode && mInitH === mediaInitH
            })
            .toArray()
          if (orphanMedia.length > 0) {
            await this.db.mediaFiles.bulkDelete(orphanMedia.map(m => m.id))
            console.log(`🗑️ Media eliminado con schedule obsoleto: ${local.title}`)
          }
          results.deleted++
          console.log(`🗑️ Schedule obsoleto eliminado: ${local.title} (${new Date(local.initH).toLocaleTimeString()})`)
        } catch (e) {
          results.errors.push({ title: local.title, error: e.message })
        }
      }
    }

    // ── 2. Agregar nuevos o actualizar existentes ────────────────────────────
    for (const [key, server] of serverMap) {
      try {
        const local = localMap.get(key)

        const scheduleData = {
          spotCode: server.spotCode,
          title:    server.title,
          url:      server.url,
          initH:    server.initH,
          endH:     server.endH,
          numerodia: server.numerodia,
          slot:     server.slot,
          tipo:     server.tipo || 'spot',
          updatedAt: now
        }

        if (!local) {
          // Nuevo: no existía localmente
          scheduleData.createdAt = now
          await this.db.spotSchedule.add(scheduleData)
          results.added++
          console.log(`✅ Nuevo schedule: ${server.title} (${new Date(server.initH).toLocaleTimeString()})`)
        } else {
          // Verificar TODOS los campos que pueden cambiar en el servidor
          const changed =
            local.url      !== server.url      ||
            local.title    !== server.title    ||
            local.slot     !== server.slot     ||
            local.tipo     !== (server.tipo || 'spot') ||
            normalizeEndH(local.endH) !== server.endH ||
            local.numerodia !== server.numerodia

          if (changed) {
            await this.db.spotSchedule.update(local.id, scheduleData)
            results.updated++
            console.log(`🔄 Schedule actualizado: ${server.title} (${new Date(server.initH).toLocaleTimeString()})`)

            // Si cambió la URL, invalidar el media descargado para forzar re-descarga
            if (local.url !== server.url) {
              const mediaInitH = server.initH
              const staleMedia = await this.db.mediaFiles
                .where('type').equals('spot')
                .and(m => {
                  const mInitH = normalizeInitH(m.initH)
                  return m.code === server.spotCode && mInitH === mediaInitH
                })
                .toArray()
              if (staleMedia.length > 0) {
                await this.db.mediaFiles.bulkDelete(staleMedia.map(m => m.id))
                console.log(`🗑️ Media invalidado por cambio de URL: ${server.title}`)
              }
            }
          } else {
            results.unchanged++
          }
        }
      } catch (e) {
        results.errors.push({ title: server.title, error: e.message })
      }
    }

    console.log(`📊 Sync schedules: +${results.added} nuevos | ~${results.updated} actualizados | =${results.unchanged} sin cambios | -${results.deleted} eliminados`)
    return results
  }
  // ── saveSpotSchedules ────────────────────────────────────────────────────
  // (implementación movida arriba con lógica de sincronización completa)

  /** @deprecated Reemplazado por saveSpotSchedules — la eliminación de obsoletos
   *  ahora ocurre dentro del mismo ciclo de sincronización.
   */
  async cleanupObsoleteSchedules(_validCodes) {
    // No-op: la limpieza ya la hace saveSpotSchedules
    return 0
  }

  /**
   * Obtiene la programación de spots
   * @param {Object} filters - Filtros opcionales
   * @returns {Array} Programación de spots
   */
  async getSpotSchedules(filters = {}) {
    let query = this.db.spotSchedule;

    if (filters.numerodia !== undefined) {
      query = query.where('numerodia').equals(filters.numerodia);
    }

    const schedules = await query.toArray();
    return schedules;
  }

  /**
   * Descarga archivos de medios para spots programados
   * Solo descarga si no existe o si cambió la URL
   * @param {Array} spotSchedules - Programación de spots
   * @returns {Object} Resultado de la descarga
   */
  async downloadSpotMedia(spotSchedules) {
    if (!Array.isArray(spotSchedules) || spotSchedules.length === 0) {
      console.warn('downloadSpotMedia: no hay spots para descargar');
      return { downloaded: 0, skipped: 0, failed: 0 };
    }

    const results = {
      downloaded: 0,
      skipped: 0,
      failed: 0,
      errors: []
    };

    for (const schedule of spotSchedules) {
      try {
        // Normalizar initH del schedule
        const scheduleInitH = typeof schedule.initH === 'number'
          ? schedule.initH
          : new Date(schedule.initH).getTime();

        // Verificar si ya existe un archivo con el mismo code Y la misma hora de inicio
        const existingMedia = await this.db.mediaFiles
          .where('type').equals('spot')
          .and(item => {
            if (item.code !== schedule.spotCode) return false;

            const itemInitH = item.initH instanceof Date
              ? item.initH.getTime()
              : (typeof item.initH === 'number' ? item.initH : new Date(item.initH).getTime());

            return itemInitH === scheduleInitH;
          })
          .first();

        // Verificar si es stream por URL o por tipo de media
        const isStreamByUrl = this.isStreamUrl(schedule.url);
        const isStreamByType = schedule.tipo && schedule.tipo.toLowerCase() === 'streaming';
        const isStream = isStreamByUrl || isStreamByType;

        if (existingMedia) {
          // Verificar si cambió la URL
          if (existingMedia.url === schedule.url) {
            // Si ya tiene blob (o es stream), no re-descargar
            if (existingMedia.blob || isStream) {
              results.skipped++;
              //(`⊘ Media ya existe: ${schedule.title} (${new Date(scheduleInitH).toLocaleTimeString()})`);
              continue;
            }
            // Tiene el registro pero le falta el blob → re-descargar
            console.warn(`⚠️ Media sin blob detectada, re-descargando: ${schedule.title}`);
            await this.db.mediaFiles.delete(existingMedia.id);
          } else {
            // URL cambió, eliminar el viejo y descargar nuevo
            //(`🔄 URL cambió para ${schedule.title}, reemplazando...`);
            await this.db.mediaFiles.delete(existingMedia.id);
          }
        }

        // Descargar nuevo archivo de medio
        let blob = null;
        let size = 0;

        if (!isStream) {
          try {
            blob = await getBlobFromUrl(schedule.url);
          } catch (downloadError) {
            console.error(`⚠️ Error de descarga para ${schedule.title}:`, downloadError.message);
            results.failed++;
            results.errors.push({
              title: schedule.title,
              error: `Error de descarga: ${downloadError.message}`
            });
            continue; // Continuar con el siguiente archivo
          }

          if (!blob) {
            console.error(`⚠️ Blob vacío para ${schedule.title}`);
            results.failed++;
            results.errors.push({
              title: schedule.title,
              error: 'Blob vacío o inválido'
            });
            continue; // Continuar con el siguiente archivo
          }
          size = blob.size || 0;
        } else {
          //(`📡 Stream detectado (tipo: ${schedule.tipo || 'por URL'}): ${schedule.title}`);
        }

        // Guardar archivo de medio con la hora de inicio asociada
        const mediaData = {
          code: schedule.spotCode,
          title: schedule.title,
          url: schedule.url,
          initH: scheduleInitH, // ✅ IMPORTANTE: Guardar initH para evitar duplicados
          endH: schedule.endH,
          slot: schedule.slot,
          numerodia: schedule.numerodia,
          blob: blob,
          type: 'spot',
          typeFile: schedule.tipo || (isStream ? 'streaming' : 'audio'),
          size: size,
          createdAt: new Date(),
          lastPlayed: null
        };

        await this.db.mediaFiles.add(mediaData);
        results.downloaded++;

        if (isStream) {
          //(`📡 Stream guardado: ${schedule.title}`);
        } else {
          //(`✅ Media descargado: ${schedule.title} (${this.formatSize(size)})`);
        }
      } catch (error) {
        console.error(`Error descargando media ${schedule.title}:`, error);
        results.failed++;
        results.errors.push({
          title: schedule.title,
          error: error.message
        });
      }
    }

    //(`📥 Descarga de medios: ${results.downloaded} descargados, ${results.skipped} omitidos, ${results.failed} fallidos`);
    return results;
  }

  /**
   * Limpia spots duplicados de la base de datos
   * Mantiene solo el registro más reciente de cada spot (code + initH)
   * @returns {Number} Cantidad de duplicados eliminados
   */
  async cleanupDuplicateSpots() {
    const allSpots = await this.db.mediaFiles.where('type').equals('spot').toArray();

    if (allSpots.length === 0) {
      //('✓ No hay spots para verificar');
      return 0;
    }

    //(`🔍 Verificando ${allSpots.length} spots en busca de duplicados...`);

    // Agrupar spots por clave única (code + initH)
    const spotGroups = new Map();

    allSpots.forEach(spot => {
      const spotInitH = spot.initH instanceof Date
        ? spot.initH.getTime()
        : (typeof spot.initH === 'number' ? spot.initH : new Date(spot.initH).getTime());

      const key = `${spot.code}_${spotInitH}`;

      if (!spotGroups.has(key)) {
        spotGroups.set(key, []);
      }
      spotGroups.get(key).push(spot);
    });

    // Identificar y eliminar duplicados (mantener el más reciente)
    const toDelete = [];

    spotGroups.forEach((spots, key) => {
      if (spots.length > 1) {
        // Ordenar por fecha de creación (más reciente primero)
        spots.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB - dateA;
        });

        // Mantener el primero (más reciente), eliminar el resto
        const [keep, ...duplicates] = spots;
        const spotTime = keep.initH instanceof Date ? keep.initH : new Date(keep.initH);

        //(`🔄 Duplicados encontrados para "${keep.title}" (${spotTime.toLocaleTimeString()}): ${duplicates.length} copias`);

        duplicates.forEach(dup => toDelete.push(dup.id));
      }
    });

    if (toDelete.length > 0) {
      await this.db.mediaFiles.bulkDelete(toDelete);
      //(`✅ Eliminados ${toDelete.length} spots duplicados`);
    } else {
      //('✓ No se encontraron duplicados');
    }

    return toDelete.length;
  }

  /**
   * Limpia archivos de medios huérfanos (sin programación asociada)
   * Considera tanto el código como la hora de inicio para determinar si es huérfano
   * @returns {Number} Cantidad eliminada
   */
  async cleanupOrphanMedia() {
    const allMedia = await this.db.mediaFiles.where('type').equals('spot').toArray();
    const allSchedules = await this.db.spotSchedule.toArray();

    // Crear un Set de claves únicas para búsqueda rápida (code_initH)
    const scheduledKeys = new Set(
      allSchedules.map(s => {
        const normalizedInitH = typeof s.initH === 'number'
          ? s.initH
          : new Date(s.initH).getTime();
        return `${s.spotCode}_${normalizedInitH}`;
      })
    );

    // Identificar huérfanos: medios que no tienen programación correspondiente
    const orphans = allMedia.filter(media => {
      const mediaInitH = media.initH instanceof Date
        ? media.initH.getTime()
        : (typeof media.initH === 'number' ? media.initH : new Date(media.initH).getTime());
      const key = `${media.code}_${mediaInitH}`;
      return !scheduledKeys.has(key);
    });

    if (orphans.length > 0) {
      const ids = orphans.map(m => m.id);
      await this.db.mediaFiles.bulkDelete(ids);
      //(`🗑️ Eliminados ${orphans.length} archivos de medios huérfanos`);

      // Log detallado de los huérfanos eliminados
      orphans.forEach(orphan => {
        const orphanTime = orphan.initH instanceof Date
          ? orphan.initH
          : new Date(orphan.initH);
        //(`   - ${orphan.title} (${orphanTime.toLocaleTimeString()})`);
      });
    }

    return orphans.length;
  }
}

// Singleton
let instance = null;
export const getLocalMediaService = () => {
  if (!instance) {
    instance = new LocalMediaService();
  }
  return instance;
};

export default LocalMediaService;
